import { useEffect, useRef, type JSX } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * One-time consent primer ("show your face", ADR-0030), shown the FIRST time a player turns their camera
 * on — before the browser's getUserMedia prompt. It frames that otherwise-jarring OS dialog as a chosen,
 * understood act and sets the tone the whole feature rests on: nearby only, never recorded, hide anytime.
 * "Show my face" proceeds to the real prompt; "Not now" backs out. Shown once (persisted), then the camera
 * button is a direct toggle. Focus-managed dialog (focus the primary action; Esc dismisses) for a11y.
 */

const PRIMER_SEEN_KEY = 'duck.camera.primerSeen';

export function readCameraPrimerSeen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(PRIMER_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

export function markCameraPrimerSeen(): void {
  try {
    window.localStorage.setItem(PRIMER_SEEN_KEY, '1');
  } catch {
    /* storage unavailable — the primer just shows again next time, which is harmless */
  }
}

export interface CameraPrimerProps {
  /** Proceed: mark seen + start the camera (triggers the browser permission prompt). */
  onConfirm: () => void;
  /** Back out without enabling the camera. */
  onDismiss: () => void;
}

export function CameraPrimer({ onConfirm, onDismiss }: CameraPrimerProps): JSX.Element {
  const reduceMotion = useReducedMotion();
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Focus the primary action on open + let Esc back out (a kind, no-friction exit).
  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="camera-primer-title"
        initial={reduceMotion ? false : { opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900/95 p-5 text-white shadow-2xl"
      >
        <h2 id="camera-primer-title" className="text-lg font-semibold">
          <span aria-hidden="true">📷</span> Show your face?
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-white/75">
          Show your real face to the ducks standing near you. Only people in your room can see it — it’s{' '}
          <span className="font-medium text-white">never recorded</span>, and you can hide again anytime.
          Your browser will ask for camera access next.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            Not now
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          >
            Show my face
          </button>
        </div>
      </motion.div>
    </div>
  );
}
